import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { downloadFromServer } from "@/lib/downloadBlob";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ChevronLeft, Upload, Brain, RefreshCw, Sparkles, Download, Loader2, FileSpreadsheet, Volume2, Headphones } from "lucide-react";

const AIBadge = ({ status }) => {
  if (status === "generated") return <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-100">Rationale Ready</Badge>;
  if (status === "needs_review") return <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">Needs Review</Badge>;
  return <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">No Rationale</Badge>;
};

const emptyQ = { question_text: "", choice_a: "", choice_b: "", choice_c: "", choice_d: "", correct_answer: "A", keyword: "", topic_tag: "", competency: "", difficulty_level: "Average", rationale: "" };

export default function QuestionsPage() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [aiFilter, setAiFilter] = useState("all");
  const [diffFilter, setDiffFilter] = useState("all");
  const [loadingList, setLoadingList] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyQ);
  const [viewing, setViewing] = useState(null);            // { question, ai }
  const [viewingLoading, setViewingLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Standalone "Import Format Guide" dialog — opens BEFORE the file
  // picker so admins see the required Excel/CSV column layout in advance.
  // Previously the guide only appeared inside the post-upload preview
  // dialog, which meant admins uploaded a wrongly-shaped file first and
  // only saw the layout afterwards.
  const [formatGuideOpen, setFormatGuideOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [bulkGen, setBulkGen] = useState(false);
  // Single-question + bulk TTS state (mirrors the AI-explanation pattern
  // right above). tts state per question is stored on the row itself as
  // `q.tts = { status: "ready" | "failed", ... }` so we can show a badge.
  const [ttsId, setTtsId] = useState(null);
  const [bulkTts, setBulkTts] = useState(false);
  const [bulkTtsJob, setBulkTtsJob] = useState(null);
  const bulkTtsPollRef = useRef(null);
  // Podcast Mode bulk pre-cache — separate job track, uses no-explanation script
  const [bulkPodcast, setBulkPodcast] = useState(false);
  const [bulkPodcastJob, setBulkPodcastJob] = useState(null);
  const bulkPodcastPollRef = useRef(null);
  const fileRef = useRef(null);
  const xlsxRef = useRef(null);

  const load = async () => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams({
        module_id: moduleId,
        page: String(page),
        page_size: String(pageSize),
      });
      if (search.trim()) params.set("search", search.trim());
      if (aiFilter !== "all") params.set("ai_status", aiFilter);
      if (diffFilter !== "all") params.set("difficulty", diffFilter);
      const r = await api.get(`/questions?${params.toString()}`);
      // Backend returns either { items, total } (paginated) or [] (legacy)
      if (Array.isArray(r.data)) {
        setItems(r.data); setTotal(r.data.length);
      } else {
        setItems(r.data.items || []); setTotal(r.data.total || 0);
      }
    } finally {
      setLoadingList(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [moduleId, page, pageSize, aiFilter, diffFilter]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 350); /* eslint-disable-line react-hooks/exhaustive-deps */
    return () => clearTimeout(t);
  }, [search]);

  const openAiView = async (q) => {
    setViewing({ question: q, ai: null });
    setViewingLoading(true);
    try {
      const r = await api.get(`/questions/${q.id}/ai-explanation`);
      setViewing({ question: q, ai: r.data });
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("No rationale generated yet for this question.");
      } else {
        toast.error("Failed to load rationale.");
      }
      setViewing(null);
    } finally {
      setViewingLoading(false);
    }
  };

  const reset = () => { setEditing(null); setForm(emptyQ); };

  const save = async () => {
    try {
      const payload = { ...form, module_id: moduleId };
      if (editing) { await api.patch(`/questions/${editing.id}`, payload); toast.success("Updated"); }
      else {
        const r = await api.post("/questions", payload);
        toast.success("Created");
        if (r.data.warning) toast.warning(r.data.warning);
      }
      setOpen(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this ENTIRE question (and its rationale + audio)? This cannot be undone.")) return;
    await api.delete(`/questions/${id}`); toast.success("Deleted"); load();
  };

  const downloadTemplate = () => {
    downloadFromServer("/questions/csv-template", "prepmasters_questions_template.csv");
  };

  const uploadCsv = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("module_id", moduleId);
    try {
      const r = await api.post("/questions/csv-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data); setImportOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const downloadXlsxTemplate = () => {
    downloadFromServer("/questions/xlsx-template", "prepmasters_questions_template.xlsx");
  };

  const uploadXlsx = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("module_id", moduleId);
    try {
      const r = await api.post("/questions/xlsx-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data); setImportOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const confirmImport = async () => {
    try {
      const r = await api.post("/questions/csv-import", { valid: preview.valid });
      toast.success(`Imported ${r.data.imported} questions`);
      setImportOpen(false); setPreview(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  const downloadErrors = () => {
    if (!preview?.invalid?.length) return;
    const rows = [["Row", "Error", "Question", "ChoiceA", "ChoiceB", "ChoiceC", "ChoiceD", "Correct", "Keyword"]];
    preview.invalid.forEach((r) => rows.push([r.row, r.error, r.data.question_text, r.data.choice_a, r.data.choice_b, r.data.choice_c, r.data.choice_d, r.data.correct_answer, r.data.keyword]));
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "import_errors.csv"; a.click();
  };

  const genAi = async (qid) => {
    setGeneratingId(qid);
    try { await api.post(`/questions/${qid}/generate-ai`); toast.success("Rationale generated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rationale generation failed"); }
    finally { setGeneratingId(null); }
  };

  const regenAi = async (qid) => {
    setGeneratingId(qid);
    try { await api.post(`/questions/${qid}/regenerate-ai`); toast.success("Rationale regenerated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setGeneratingId(null); }
  };

  const delAi = async (qid) => {
    if (!window.confirm("Delete only the AI-generated Rationale for this question? The question itself will remain.")) return;
    await api.delete(`/questions/${qid}/ai`); toast.success("Rationale deleted"); load();
  };

  // Bulk AI generation runs as a background job. We kick it off, then poll
  // status every 2s and surface live progress. Page stays interactive.
  const [bulkJob, setBulkJob] = useState(null);  // { job_id, total, done, failed, status, progress_pct }
  const bulkPollRef = useRef(null);

  const pollBulkStatus = async () => {
    try {
      const url = bulkJob?.job_id
        ? `/modules/${moduleId}/generate-ai/status?job_id=${bulkJob.job_id}`
        : `/modules/${moduleId}/generate-ai/status`;
      const r = await api.get(url);
      const j = r.data;
      setBulkJob(j);
      if (j.status === "completed" || j.status === "failed" || j.status === "idle") {
        clearInterval(bulkPollRef.current); bulkPollRef.current = null;
        setBulkGen(false);
        if (j.status === "completed") toast.success(`Rationale generation complete: ${j.done} succeeded${j.failed ? `, ${j.failed} failed` : ""}.`);
        if (j.status === "failed") toast.error(`Rationale generation failed: ${j.error || "Unknown error"}`);
        load();
      }
    } catch (e) {
      clearInterval(bulkPollRef.current); bulkPollRef.current = null;
      setBulkGen(false);
      toast.error("Lost connection to rationale job status.");
    }
  };

  const bulkGenerate = async (onlyMissing) => {
    setBulkGen(true);
    try {
      const r = await api.post(`/modules/${moduleId}/generate-ai?only_missing=${onlyMissing}`);
      const j = r.data;
      setBulkJob(j);
      if (j.total === 0) {
        toast.info("No questions need rationale generation in this module.");
        setBulkGen(false);
        return;
      }
      if (j.status === "completed") {
        toast.success(`AI generated for ${j.done} question(s).`);
        setBulkGen(false);
        load();
        return;
      }
      toast.info(`Started: generating ${j.total} explanation(s) in the background. You can keep working.`);
      // Poll every 2s
      bulkPollRef.current = setInterval(pollBulkStatus, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start rationale generation");
      setBulkGen(false);
    }
  };

  // ---- TTS (audio) generation — mirrors the AI-explanation pattern above.
  //      Uses /api/tts/audio behind the scenes; the admin button just tells
  //      the server to prime the cache so students never wait for OpenAI.
  const genTts = async (qid) => {
    setTtsId(qid);
    try {
      await api.post(`/questions/${qid}/generate-tts`);
      toast.success("Audio ready — served instantly to students next time.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TTS generation failed");
    } finally { setTtsId(null); }
  };

  const pollBulkTtsStatus = async () => {
    try {
      const url = bulkTtsJob?.job_id
        ? `/modules/${moduleId}/generate-tts/status?job_id=${bulkTtsJob.job_id}`
        : `/modules/${moduleId}/generate-tts/status`;
      const r = await api.get(url);
      const j = r.data;
      setBulkTtsJob(j);
      if (j.status === "completed" || j.status === "failed" || j.status === "idle") {
        clearInterval(bulkTtsPollRef.current); bulkTtsPollRef.current = null;
        setBulkTts(false);
        if (j.status === "completed") toast.success(`TTS pre-cache complete: ${j.done} succeeded${j.failed ? `, ${j.failed} failed` : ""}.`);
        if (j.status === "failed") toast.error(`TTS pre-cache failed: ${j.error || "Unknown error"}`);
        load();
      }
    } catch {
      clearInterval(bulkTtsPollRef.current); bulkTtsPollRef.current = null;
      setBulkTts(false);
      toast.error("Lost connection to TTS job status.");
    }
  };

  const bulkGenerateTts = async (onlyMissing) => {
    setBulkTts(true);
    try {
      const r = await api.post(`/modules/${moduleId}/generate-tts?only_missing=${onlyMissing}`);
      const j = r.data;
      setBulkTtsJob(j);
      if (j.total === 0) {
        toast.info("All questions already have cached audio for this module.");
        setBulkTts(false);
        return;
      }
      toast.info(`Started: caching audio for ${j.total} question(s) in the background.`);
      bulkTtsPollRef.current = setInterval(pollBulkTtsStatus, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start TTS pre-cache");
      setBulkTts(false);
    }
  };

  // -------------------- Podcast Mode bulk pre-cache --------------------
  const pollBulkPodcastStatus = async () => {
    try {
      const url = bulkPodcastJob?.job_id
        ? `/modules/${moduleId}/generate-podcast/status?job_id=${bulkPodcastJob.job_id}`
        : `/modules/${moduleId}/generate-podcast/status`;
      const r = await api.get(url);
      const j = r.data;
      setBulkPodcastJob(j);
      if (j.status === "completed" || j.status === "failed" || j.status === "idle") {
        clearInterval(bulkPodcastPollRef.current); bulkPodcastPollRef.current = null;
        setBulkPodcast(false);
        if (j.status === "completed") toast.success(`Podcast audio ready: ${j.done} succeeded${j.failed ? `, ${j.failed} failed` : ""}.`);
        if (j.status === "failed") toast.error(`Podcast pre-cache failed: ${j.error || "Unknown error"}`);
        load();
      }
    } catch {
      clearInterval(bulkPodcastPollRef.current); bulkPodcastPollRef.current = null;
      setBulkPodcast(false);
      toast.error("Lost connection to Podcast job status.");
    }
  };

  const bulkGeneratePodcast = async (onlyMissing) => {
    setBulkPodcast(true);
    try {
      const r = await api.post(`/modules/${moduleId}/generate-podcast?only_missing=${onlyMissing}`);
      const j = r.data;
      setBulkPodcastJob(j);
      if (j.total === 0) {
        toast.info("All questions already have podcast audio ready for this module.");
        setBulkPodcast(false);
        return;
      }
      toast.info(`Started: caching podcast audio for ${j.total} question(s).`);
      bulkPodcastPollRef.current = setInterval(pollBulkPodcastStatus, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Podcast pre-cache");
      setBulkPodcast(false);
    }
  };

  // On mount, check if an AI job is already running for this module (e.g.
  // admin navigated away and came back) — resume polling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/modules/${moduleId}/generate-ai/status`);
        if (cancelled) return;
        if (r.data?.status === "running") {
          setBulkJob(r.data); setBulkGen(true);
          bulkPollRef.current = setInterval(pollBulkStatus, 2000);
        }
      } catch { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [moduleId]);

  const renderQuestionWithKeyword = (text, keyword) => {
    if (!keyword || !text.toLowerCase().includes(keyword.toLowerCase())) return text;
    const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
    return (<>{text.slice(0, idx)}<span className="keyword-highlight">{text.slice(idx, idx + keyword.length)}</span>{text.slice(idx + keyword.length)}</>);
  };

  return (
    <div className="space-y-6" data-testid="questions-page">
      <Link to="/admin/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back to Courses</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Module</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Questions</h1>
          <p className="text-slate-600 mt-1">{total} question(s){loadingList ? " · loading…" : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {hasPerm(user, "ai_explanation", "generate") && (
            <>
              <Button variant="outline" disabled={bulkGen} onClick={() => bulkGenerate(true)} data-testid="gen-ai-missing-btn"><Sparkles className="w-4 h-4 mr-1" />{bulkGen ? "Generating…" : "Generate Rationale (missing)"}</Button>
              <Button variant="outline" disabled={bulkGen} onClick={() => bulkGenerate(false)} data-testid="gen-ai-all-btn"><Brain className="w-4 h-4 mr-1" />Generate Rationale (all)</Button>
              {bulkGen && bulkJob && (
                <div
                  className="px-3 py-1.5 rounded-full border border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-700 text-xs font-semibold text-indigo-700 dark:text-indigo-200 inline-flex items-center gap-2"
                  data-testid="ai-bulk-progress"
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  AI · {bulkJob.done}/{bulkJob.total}
                  {bulkJob.failed > 0 ? ` (${bulkJob.failed} failed)` : ""} · {bulkJob.progress_pct}%
                </div>
              )}
              {/* Bulk TTS pre-cache — parallel controls to the AI ones above.
                  Pre-generates MP3 audio files so students hear instant
                  playback with zero OpenAI cost on repeat views. */}
              <Button variant="outline" disabled={bulkTts} onClick={() => bulkGenerateTts(true)} data-testid="gen-tts-missing-btn"><Volume2 className="w-4 h-4 mr-1" />{bulkTts ? "Caching…" : "Generate Audio (missing)"}</Button>
              <Button variant="outline" disabled={bulkTts} onClick={() => bulkGenerateTts(false)} data-testid="gen-tts-all-btn"><Volume2 className="w-4 h-4 mr-1" />Generate Audio (all)</Button>
              {bulkTts && bulkTtsJob && (
                <div
                  className="px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700 text-xs font-semibold text-emerald-700 dark:text-emerald-200 inline-flex items-center gap-2"
                  data-testid="tts-bulk-progress"
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Audio · {bulkTtsJob.done}/{bulkTtsJob.total}
                  {bulkTtsJob.failed > 0 ? ` (${bulkTtsJob.failed} failed)` : ""} · {bulkTtsJob.progress_pct}%
                </div>
              )}
              {/* Podcast Mode pre-cache — questions + choices + "the answer is X"
                  (no explanation). Separate job track from the Read-Aloud
                  bulk-TTS so admins can pre-warm both in parallel. */}
              <Button variant="outline" disabled={bulkPodcast} onClick={() => bulkGeneratePodcast(true)} data-testid="gen-podcast-missing-btn" className="border-amber-300 text-amber-800 hover:bg-amber-50"><Headphones className="w-4 h-4 mr-1" />{bulkPodcast ? "Caching…" : "Generate Podcast (missing)"}</Button>
              <Button variant="outline" disabled={bulkPodcast} onClick={() => bulkGeneratePodcast(false)} data-testid="gen-podcast-all-btn" className="border-amber-300 text-amber-800 hover:bg-amber-50"><Headphones className="w-4 h-4 mr-1" />Generate Podcast (all)</Button>
              {bulkPodcast && bulkPodcastJob && (
                <div
                  className="px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 text-xs font-semibold text-amber-800 dark:text-amber-200 inline-flex items-center gap-2"
                  data-testid="podcast-bulk-progress"
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Podcast · {bulkPodcastJob.done}/{bulkPodcastJob.total}
                  {bulkPodcastJob.failed > 0 ? ` (${bulkPodcastJob.failed} failed)` : ""} · {bulkPodcastJob.progress_pct}%
                </div>
              )}
            </>
          )}
          {hasPerm(user, "question", "add") && (
            <>
              <Button variant="outline" onClick={() => setFormatGuideOpen(true)} data-testid="format-guide-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Import </span>Format Guide
              </Button>
              <Button variant="outline" onClick={downloadTemplate} data-testid="csv-template-btn"><Download className="w-4 h-4 mr-1" />CSV Template</Button>
              <Button variant="outline" onClick={downloadXlsxTemplate} data-testid="xlsx-template-btn"><Download className="w-4 h-4 mr-1" />Excel Template</Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="csv-import-btn"><Upload className="w-4 h-4 mr-1" />Import CSV</Button>
              <Button variant="outline" onClick={() => xlsxRef.current?.click()} data-testid="xlsx-import-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Import Excel</Button>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadCsv(f); e.target.value = ""; } }} />
              <input ref={xlsxRef} type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadXlsx(f); e.target.value = ""; } }} />
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                <DialogTrigger asChild><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="add-question-btn"><Plus className="w-4 h-4 mr-2" />New Question</Button></DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editing ? "Edit Question" : "New Question"}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Question text</Label><Textarea rows={3} value={form.question_text} onChange={(e) => setForm({ ...form, question_text: e.target.value })} data-testid="q-text" /></div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><Label>Choice A *</Label><Input value={form.choice_a} onChange={(e) => setForm({ ...form, choice_a: e.target.value })} data-testid="q-a" /></div>
                      <div><Label>Choice B *</Label><Input value={form.choice_b} onChange={(e) => setForm({ ...form, choice_b: e.target.value })} data-testid="q-b" /></div>
                      <div><Label>Choice C</Label><Input value={form.choice_c} onChange={(e) => setForm({ ...form, choice_c: e.target.value })} data-testid="q-c" /></div>
                      <div><Label>Choice D</Label><Input value={form.choice_d} onChange={(e) => setForm({ ...form, choice_d: e.target.value })} data-testid="q-d" /></div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><Label>Correct answer</Label>
                        <Select value={form.correct_answer} onValueChange={(v) => setForm({ ...form, correct_answer: v })}>
                          <SelectTrigger data-testid="q-correct"><SelectValue /></SelectTrigger>
                          <SelectContent>{["A", "B", "C", "D"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Keyword (to highlight)</Label><Input value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} data-testid="q-keyword" /></div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div><Label>Topic Tag</Label><Input value={form.topic_tag} onChange={(e) => setForm({ ...form, topic_tag: e.target.value })} placeholder="e.g. Photosynthesis" data-testid="q-topic" /></div>
                      <div><Label>Competency</Label><Input value={form.competency} onChange={(e) => setForm({ ...form, competency: e.target.value })} placeholder="e.g. Analyze concepts" data-testid="q-competency" /></div>
                      <div><Label>Difficulty</Label>
                        <Select value={form.difficulty_level} onValueChange={(v) => setForm({ ...form, difficulty_level: v })}>
                          <SelectTrigger data-testid="q-difficulty"><SelectValue /></SelectTrigger>
                          <SelectContent>{["Easy", "Average", "Difficult"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Rationale (shown to students after they answer)</Label>
                      <Textarea
                        rows={3}
                        value={form.rationale}
                        onChange={(e) => setForm({ ...form, rationale: e.target.value })}
                        placeholder="Explain WHY the correct answer is correct (and why the distractors are wrong). Optional but recommended."
                        data-testid="q-rationale"
                      />
                    </div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={save} data-testid="q-save">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filter / search toolbar */}
      <Card className="p-3 border-slate-200">
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            placeholder="Search question / keyword / topic…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 max-w-sm"
            data-testid="q-search-input"
          />
          <Select value={aiFilter} onValueChange={(v) => { setAiFilter(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="q-filter-ai"><SelectValue placeholder="Rationale status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="generated">Rationale Ready</SelectItem>
              <SelectItem value="needs_review">Needs Review</SelectItem>
              <SelectItem value="no_ai">No Rationale</SelectItem>
            </SelectContent>
          </Select>
          <Select value={diffFilter} onValueChange={(v) => { setDiffFilter(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="q-filter-diff"><SelectValue placeholder="Difficulty" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All difficulty</SelectItem>
              <SelectItem value="Easy">Easy</SelectItem>
              <SelectItem value="Average">Average</SelectItem>
              <SelectItem value="Difficult">Difficult</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-2 text-sm text-slate-600">
            <span>Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-9 w-[80px]" data-testid="q-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {items.length === 0 ? <Card className="p-8 text-center text-slate-500">{loadingList ? "Loading…" : "No questions match your filters."}</Card> :
          items.map((q, i) => (
            <Card key={q.id} className="p-5 border-slate-200" data-testid={`q-row-${q.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-500">Q{(page - 1) * pageSize + i + 1}</span>
                    <AIBadge status={q.ai_status} />
                    <Badge variant="outline" className="text-xs">Correct: {q.correct_answer}</Badge>
                    {q.difficulty_level && <Badge variant="outline" className={`text-xs ${q.difficulty_level === "Easy" ? "border-green-300 text-green-700 bg-green-50" : q.difficulty_level === "Difficult" ? "border-red-300 text-red-700 bg-red-50" : "border-blue-300 text-blue-700 bg-blue-50"}`}>{q.difficulty_level}</Badge>}
                    {q.topic_tag && <Badge variant="outline" className="text-xs">{q.topic_tag}</Badge>}
                  </div>
                  <div className="text-slate-900 leading-relaxed">{renderQuestionWithKeyword(q.question_text, q.keyword)}</div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                    {["a", "b", "c", "d"].map((c) => q[`choice_${c}`] && (
                      <div key={c} className={`p-2 rounded border ${q.correct_answer === c.toUpperCase() ? "border-green-300 bg-green-50" : "border-slate-200"}`}>
                        <span className="font-semibold">{c.toUpperCase()}.</span> {q[`choice_${c}`]}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {hasPerm(user, "ai_explanation", "view") && q.ai_status !== "no_ai" && <Button size="sm" variant="outline" onClick={() => openAiView(q)} data-testid={`view-ai-${q.id}`}>View Rationale</Button>}
                  {hasPerm(user, "ai_explanation", "generate") && q.ai_status === "no_ai" && <Button size="sm" disabled={generatingId === q.id} onClick={() => genAi(q.id)} data-testid={`gen-ai-${q.id}`}>{generatingId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Brain className="w-4 h-4 mr-1" />Generate Rationale</>}</Button>}
                  {hasPerm(user, "ai_explanation", "regenerate") && q.ai_status !== "no_ai" && <Button size="sm" variant="outline" disabled={generatingId === q.id} onClick={() => regenAi(q.id)} data-testid={`regen-${q.id}`} title="Regenerate the AI Rationale for this question">{generatingId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" />Regen Rationale</>}</Button>}
                  {/* Row-level TTS. Green pill when audio is cached; a "Generate Audio"
                      button appears otherwise. Uses the same permission gate as AI. */}
                  {hasPerm(user, "ai_explanation", "generate") && q.tts?.status === "ready" && (
                    <Button size="sm" variant="outline" disabled={ttsId === q.id} onClick={() => genTts(q.id)} data-testid={`regen-tts-${q.id}`} className="border-emerald-300 text-emerald-800">
                      {ttsId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Volume2 className="w-4 h-4 mr-1" />Audio ✓</>}
                    </Button>
                  )}
                  {hasPerm(user, "ai_explanation", "generate") && q.tts?.status !== "ready" && (
                    <Button size="sm" variant="outline" disabled={ttsId === q.id} onClick={() => genTts(q.id)} data-testid={`gen-tts-${q.id}`}>
                      {ttsId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Volume2 className="w-4 h-4 mr-1" />Generate Audio</>}
                    </Button>
                  )}
                  {/* Two destructive actions were previously rendered as identical
                      unlabelled red-trash icons which confused admins. Explicit
                      labels + tooltips now make each one's target unambiguous:
                        • Delete Rationale — wipes just the AI-generated rationale
                        • Delete Question — removes the whole question row */}
                  {hasPerm(user, "ai_explanation", "delete") && q.ai_status !== "no_ai" && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => delAi(q.id)} data-testid={`del-ai-${q.id}`} title="Delete only the AI-generated Rationale for this question. The question stays."><Trash2 className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Delete Rationale</span></Button>}
                  {hasPerm(user, "question", "edit") && <Button size="sm" variant="outline" onClick={() => { setEditing(q); setForm({ question_text: q.question_text, choice_a: q.choice_a, choice_b: q.choice_b, choice_c: q.choice_c || "", choice_d: q.choice_d || "", correct_answer: q.correct_answer, keyword: q.keyword || "", topic_tag: q.topic_tag || "", competency: q.competency || "", difficulty_level: q.difficulty_level || "Average", rationale: q.rationale || "" }); setOpen(true); }} data-testid={`edit-q-${q.id}`} title="Edit this question"><Edit className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Edit Question</span></Button>}
                  {hasPerm(user, "question", "delete") && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => del(q.id)} data-testid={`del-q-${q.id}`} title="Delete the entire question row. This cannot be undone."><Trash2 className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Delete Question</span></Button>}
                </div>
              </div>
            </Card>
          ))}
      </div>

      {/* Pagination footer */}
      {total > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 pt-2" data-testid="q-pagination">
          <div className="text-sm text-slate-600">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1 || loadingList} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="q-prev-btn">Previous</Button>
            <span className="text-sm text-slate-600">Page <strong>{page}</strong> of {Math.max(1, Math.ceil(total / pageSize))}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / pageSize) || loadingList} onClick={() => setPage((p) => p + 1)} data-testid="q-next-btn">Next</Button>
          </div>
        </div>
      )}

      {/* Standalone Import Format Guide — visible BEFORE file upload so
          admins get the column layout in advance (Vultr users reported
          the in-preview guide was too late in the flow). */}
      <Dialog open={formatGuideOpen} onOpenChange={setFormatGuideOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="format-guide-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              CSV / Excel Import Format
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Import maps by <b>column position</b> (Excel column letters below),
              not by header text. A header row is optional — put data on row 1
              without a header, or row 2 if you keep a header row. Every row
              becomes one question.
            </p>
            <div className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-900/40">
                  <tr>
                    <th className="p-2 text-left">Excel column</th>
                    <th className="p-2 text-left">Header (any label OK)</th>
                    <th className="p-2 text-left">Required?</th>
                    <th className="p-2 text-left">Allowed values / example</th>
                  </tr>
                </thead>
                <tbody className="[&>tr:nth-child(even)]:bg-slate-50 dark:[&>tr:nth-child(even)]:bg-slate-800/30">
                  <tr><td className="p-2 font-mono font-bold">A</td><td className="p-2">Question</td><td className="p-2 text-emerald-700 dark:text-emerald-300 font-semibold">Required</td><td className="p-2">Full question stem — e.g. <i>"What is the primary purpose of assessment?"</i></td></tr>
                  <tr><td className="p-2 font-mono font-bold">B</td><td className="p-2">Choice A</td><td className="p-2 text-emerald-700 dark:text-emerald-300 font-semibold">Required</td><td className="p-2">Full text of option A — e.g. <i>"To grade students"</i></td></tr>
                  <tr><td className="p-2 font-mono font-bold">C</td><td className="p-2">Choice B</td><td className="p-2 text-emerald-700 dark:text-emerald-300 font-semibold">Required</td><td className="p-2">Full text of option B — e.g. <i>"To improve learning"</i></td></tr>
                  <tr><td className="p-2 font-mono font-bold">D</td><td className="p-2">Choice C</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Leave blank for 2-choice items</td></tr>
                  <tr><td className="p-2 font-mono font-bold">E</td><td className="p-2">Choice D</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Leave blank for 2- or 3-choice items</td></tr>
                  <tr><td className="p-2 font-mono font-bold">F</td><td className="p-2">Correct answer</td><td className="p-2 text-emerald-700 dark:text-emerald-300 font-semibold">Required</td><td className="p-2"><b>A</b>, <b>B</b>, <b>C</b>, or <b>D</b> — a single letter only</td></tr>
                  <tr><td className="p-2 font-mono font-bold">G</td><td className="p-2">Key word</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Short phrase used by Read Aloud — e.g. <i>"primary purpose"</i></td></tr>
                  <tr><td className="p-2 font-mono font-bold">H</td><td className="p-2">Topic tag</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">One or two words — e.g. <i>Ecology</i>, <i>Assessment</i></td></tr>
                  <tr><td className="p-2 font-mono font-bold">I</td><td className="p-2">Competency</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Free text describing the competency</td></tr>
                  <tr className="bg-amber-50 dark:bg-amber-900/20"><td className="p-2 font-mono font-bold">J</td><td className="p-2 font-semibold">Difficulty</td><td className="p-2 text-slate-500">Optional</td><td className="p-2"><b>Easy</b>, <b>Average</b>, or <b>Difficult</b> only — defaults to Average</td></tr>
                  <tr className="bg-amber-50 dark:bg-amber-900/20"><td className="p-2 font-mono font-bold">K</td><td className="p-2 font-semibold">Rationale</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">A full sentence explaining WHY the correct answer is right. Leave blank to let the AI generate it (click "Generate Rationale" after import)</td></tr>
                </tbody>
              </table>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-100">
              <div className="font-semibold mb-1">⚠ Two most common mistakes</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Column <b>J</b> is Difficulty (single word). Column <b>K</b> is Rationale (full sentence). Don't put "Easy" in K.</li>
                <li>Column <b>F</b> must be a single letter (A/B/C/D), not the full choice text. If you pasted the correct choice text there instead of the letter, the system tries to auto-match but may guess wrong.</li>
              </ul>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => { setFormatGuideOpen(false); downloadXlsxTemplate(); }} data-testid="fg-download-xlsx-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Download className="w-4 h-4 mr-2" /> Download Excel template
              </Button>
              <Button variant="outline" onClick={() => { setFormatGuideOpen(false); downloadTemplate(); }} data-testid="fg-download-csv-btn">
                <Download className="w-4 h-4 mr-2" /> Download CSV template
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setFormatGuideOpen(false)} data-testid="fg-close-btn">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Preview Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Import Preview</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-4">
              {/* Column format guide — dropped in above the preview table so
                  admins have a single canonical reference for what each
                  spreadsheet column MUST contain. Import is always by
                  column POSITION regardless of header labels, so shipping
                  a clear guide here prevents the "difficulty text ends up
                  in the Rationale field" bug reported by users. */}
              <details className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-sm" open>
                <summary className="p-3 font-semibold cursor-pointer select-none">
                  📋 Column format guide — click to collapse
                </summary>
                <div className="px-3 pb-3">
                  <p className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                    Import maps by <b>column position</b> (Excel column letters below), not by header text.
                    Header row is optional — put data on row 1 if no header, row 2 if you keep a header.
                  </p>
                  <div className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-900/40">
                        <tr>
                          <th className="p-2 text-left">Excel column</th>
                          <th className="p-2 text-left">Header</th>
                          <th className="p-2 text-left">Required?</th>
                          <th className="p-2 text-left">Allowed values / example</th>
                        </tr>
                      </thead>
                      <tbody className="[&>tr:nth-child(even)]:bg-slate-50 dark:[&>tr:nth-child(even)]:bg-slate-800/30">
                        <tr><td className="p-2 font-mono font-bold">A</td><td className="p-2">Question</td><td className="p-2 text-emerald-700 dark:text-emerald-300">Yes</td><td className="p-2">Full question stem — e.g. "What is the primary purpose of assessment?"</td></tr>
                        <tr><td className="p-2 font-mono font-bold">B</td><td className="p-2">Choice A</td><td className="p-2 text-emerald-700 dark:text-emerald-300">Yes</td><td className="p-2">Full text of option A — e.g. "To grade students"</td></tr>
                        <tr><td className="p-2 font-mono font-bold">C</td><td className="p-2">Choice B</td><td className="p-2 text-emerald-700 dark:text-emerald-300">Yes</td><td className="p-2">Full text of option B — e.g. "To improve learning"</td></tr>
                        <tr><td className="p-2 font-mono font-bold">D</td><td className="p-2">Choice C</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Leave blank for 2-choice items</td></tr>
                        <tr><td className="p-2 font-mono font-bold">E</td><td className="p-2">Choice D</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Leave blank for 2/3-choice items</td></tr>
                        <tr><td className="p-2 font-mono font-bold">F</td><td className="p-2">Correct answer</td><td className="p-2 text-emerald-700 dark:text-emerald-300">Yes</td><td className="p-2"><b>A</b>, <b>B</b>, <b>C</b>, or <b>D</b> — a single letter</td></tr>
                        <tr><td className="p-2 font-mono font-bold">G</td><td className="p-2">Key word</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Short phrase — used by Read Aloud, e.g. "primary purpose"</td></tr>
                        <tr><td className="p-2 font-mono font-bold">H</td><td className="p-2">Topic tag</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">e.g. <i>Ecology</i>, <i>Assessment</i></td></tr>
                        <tr><td className="p-2 font-mono font-bold">I</td><td className="p-2">Competency</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Free text describing the competency</td></tr>
                        <tr className="bg-amber-50 dark:bg-amber-900/20"><td className="p-2 font-mono font-bold">J</td><td className="p-2 font-semibold">Difficulty</td><td className="p-2 text-slate-500">Optional</td><td className="p-2"><b>Easy</b>, <b>Average</b>, or <b>Difficult</b> only (defaults to Average)</td></tr>
                        <tr className="bg-amber-50 dark:bg-amber-900/20"><td className="p-2 font-mono font-bold">K</td><td className="p-2 font-semibold">Rationale</td><td className="p-2 text-slate-500">Optional</td><td className="p-2">Sentence explaining WHY the correct answer is right. Leave blank to let the AI generate it — you'll see "Generate Rationale" in the admin toolbar</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-amber-800 dark:text-amber-200 mt-2">
                    ⚠ Column <b>J</b> is Difficulty (Easy/Average/Difficult). Column <b>K</b> is Rationale (a full sentence). If you accidentally put "Easy" in K, the system auto-swaps it into J.
                  </p>
                </div>
              </details>

              {preview.warning && (
                <div className="p-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-800 text-amber-900 dark:text-amber-100 text-sm flex items-start gap-2" data-testid="csv-import-warning">
                  <span className="font-bold text-amber-700 dark:text-amber-300">⚠</span>
                  <span>{preview.warning}</span>
                </div>
              )}
              <div className="flex gap-4 text-sm flex-wrap">
                <Badge className="bg-green-100 text-green-800">Valid: {preview.valid.length}</Badge>
                <Badge className="bg-red-100 text-red-800">Invalid: {preview.invalid.length}</Badge>
                <Badge variant="outline">Total: {preview.total}</Badge>
                {preview.header_recognized === false && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Using standard column order</Badge>
                )}
                {preview.invalid.length > 0 && <Button size="sm" variant="outline" onClick={downloadErrors}>Download Error Report</Button>}
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden" data-testid="csv-preview-table">
                <div className="max-h-[440px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                      <tr className="text-left">
                        <th className="p-2">#</th>
                        <th className="p-2">Status</th>
                        <th className="p-2 min-w-[200px]">Question</th>
                        <th className="p-2">A</th>
                        <th className="p-2">B</th>
                        <th className="p-2">C</th>
                        <th className="p-2">D</th>
                        <th className="p-2">Correct</th>
                        <th className="p-2">Keyword</th>
                        <th className="p-2">Topic / Comp / Diff</th>
                        <th className="p-2">Error / Warning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.valid.map((v, i) => (
                        <tr key={`v${i}`} className="even:bg-slate-50 dark:even:bg-slate-800/30">
                          <td className="p-2">{i + 1}</td>
                          <td className="p-2"><Badge className="bg-green-100 text-green-800">Valid</Badge></td>
                          <td className="p-2 max-w-xs truncate" title={v.question_text}>{v.question_text}</td>
                          <td className="p-2 max-w-[110px] truncate" title={v.choice_a}>{v.choice_a}</td>
                          <td className="p-2 max-w-[110px] truncate" title={v.choice_b}>{v.choice_b}</td>
                          <td className="p-2 max-w-[110px] truncate" title={v.choice_c}>{v.choice_c || "—"}</td>
                          <td className="p-2 max-w-[110px] truncate" title={v.choice_d}>{v.choice_d || "—"}</td>
                          <td className="p-2 font-mono font-semibold">{v.correct_answer}</td>
                          <td className="p-2 max-w-[120px] truncate" title={v.keyword}>{v.keyword || "—"}</td>
                          <td className="p-2 text-xs text-slate-600 dark:text-slate-300">
                            {[v.topic_tag, v.competency, v.difficulty_level].filter(Boolean).join(" · ") || "—"}
                          </td>
                          <td className="p-2 text-xs text-amber-700 dark:text-amber-300">{v.warning || "—"}</td>
                        </tr>
                      ))}
                      {preview.invalid.map((iv, i) => (
                        <tr key={`i${i}`} className="bg-red-50 dark:bg-red-900/20">
                          <td className="p-2">{iv.row}</td>
                          <td className="p-2"><Badge className="bg-red-100 text-red-800">Invalid</Badge></td>
                          <td className="p-2 max-w-xs truncate" title={iv.data.question_text}>{iv.data.question_text || "(blank)"}</td>
                          <td className="p-2 max-w-[110px] truncate">{iv.data.choice_a}</td>
                          <td className="p-2 max-w-[110px] truncate">{iv.data.choice_b}</td>
                          <td className="p-2 max-w-[110px] truncate">{iv.data.choice_c || "—"}</td>
                          <td className="p-2 max-w-[110px] truncate">{iv.data.choice_d || "—"}</td>
                          <td className="p-2 font-mono">{iv.data.correct_answer || "—"}</td>
                          <td className="p-2">{iv.data.keyword || "—"}</td>
                          <td className="p-2 text-xs">
                            {[iv.data.topic_tag, iv.data.competency, iv.data.difficulty_level].filter(Boolean).join(" · ") || "—"}
                          </td>
                          <td className="p-2 text-red-700 dark:text-red-300 text-xs">{iv.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={confirmImport} disabled={preview.valid.length === 0} data-testid="confirm-import-btn">Import {preview.valid.length} valid rows</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI View Dialog — fetched lazily on click */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Rationale</DialogTitle></DialogHeader>
          {viewingLoading && (
            <div className="flex items-center justify-center py-10 text-slate-500 gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading rationale…</div>
          )}
          {!viewingLoading && viewing?.ai && (
            <div className="space-y-3">
              {viewing.ai.needs_review && <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">⚠ This item is flagged: Needs Review (admin only)</div>}
              <div className="ai-card p-4">
                <div className="text-xs font-bold text-indigo-700 mb-1 uppercase tracking-wide">CORRECT ANSWER — WHY IT'S CORRECT</div>
                <div className="text-sm font-semibold text-slate-900 mb-2">{viewing.question[`choice_${viewing.question.correct_answer.toLowerCase()}`]}</div>
                <p className="text-sm leading-relaxed">{viewing.ai.correct_answer_explanation}</p>
              </div>
              {["a", "b", "c", "d"].map((c) => viewing.question[`choice_${c}`] && viewing.ai[`choice_${c}_explanation`] && viewing.question.correct_answer !== c.toUpperCase() && (
                <div key={c} className="p-4 rounded-md border border-slate-200 bg-slate-50">
                  <div className="text-xs font-bold text-slate-600 mb-1 uppercase tracking-wide">WHY THIS IS WRONG</div>
                  <div className="text-sm font-semibold text-slate-900 mb-2">{viewing.question[`choice_${c}`]}</div>
                  <p className="text-sm leading-relaxed">{viewing.ai[`choice_${c}_explanation`]}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
